import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Upload, FileText, Image, File, Trash2,
  Download, Search, Loader2, FolderOpen, Folder as FolderIcon, Link2, RefreshCw,
  Folder, ChevronRight, ChevronDown, Home, Eye, ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob, downloadFromUrl } from '../utils/download';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

// Folder tree structure (each top-level folder can have subfolders)
const FOLDER_TREE = [
  { value: 'compromis',   label: 'Compromis de vente' },
  { value: 'diagnostics', label: 'Diagnostics', subfolders: ['DPE', 'Amiante', 'Plomb', 'Electricité', 'Gaz', 'Termites', 'ERP', 'Autre'] },
  { value: 'devis',       label: 'Devis' },
  { value: 'factures',    label: 'Factures' },
  { value: 'plans',       label: 'Plans & Cadastre' },
  { value: 'assurances',  label: 'Assurances' },
  { value: 'bancaire',    label: 'Pièces bancaires' },
  { value: 'acte_vente',  label: 'Acte de vente' },
  { value: 'travaux',     label: 'Travaux / Photos chantier' },
  { value: 'autres',      label: 'Autres' },
];

const FOLDER_LABEL = Object.fromEntries(FOLDER_TREE.map(f => [f.value, f.label]));

const getFileIcon = (ct) => {
  if (ct?.startsWith('image/')) return Image;
  if (ct?.includes('pdf')) return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const FileCard = ({ file, projects, onDelete, onDownload, onAssign, onPreview }) => {
  const Icon = getFileIcon(file.content_type);
  const project = projects.find(p => p.id === file.project_id);
  const folder = FOLDER_LABEL[file.folder];
  const isImage = file.content_type?.startsWith('image/');
  const isPdf = file.content_type?.includes('pdf');

  return (
    <Card className="bg-zinc-900 border-zinc-800 card-hover overflow-hidden group">
      <button
        type="button"
        onClick={() => onPreview(file)}
        className="relative h-28 bg-zinc-800 w-full flex items-center justify-center cursor-pointer"
        data-testid={`preview-${file.id}`}
        title="Aperçu"
      >
        <Icon className="w-10 h-10 text-zinc-600" />
        {(isImage || isPdf) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors">
            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onDownload(file); }}
            className="h-7 w-7 inline-flex items-center justify-center rounded bg-zinc-700 hover:bg-zinc-600">
            <Download className="w-3 h-3 text-white" />
          </span>
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onAssign(file); }}
            className="h-7 w-7 inline-flex items-center justify-center rounded bg-zinc-700 hover:bg-zinc-600">
            <Link2 className="w-3 h-3 text-blue-400" />
          </span>
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onDelete(file.id); }}
            className="h-7 w-7 inline-flex items-center justify-center rounded bg-red-600 hover:bg-red-700">
            <Trash2 className="w-3 h-3 text-white" />
          </span>
        </div>
      </button>
      <CardContent className="p-3">
        <h4 className="text-sm font-medium text-white truncate" title={file.original_filename}>
          {file.original_filename}
        </h4>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-zinc-500">{formatFileSize(file.size)}</span>
          <span className="text-xs text-zinc-600">
            {file.created_at ? format(new Date(file.created_at), 'dd/MM/yy', { locale: fr }) : ''}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {project && <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">{project.property_title || 'Projet'}</Badge>}
          {folder && <Badge variant="outline" className="text-[10px] bg-zinc-700 text-zinc-300 border-zinc-600">{folder}{file.subfolder ? ` / ${file.subfolder}` : ''}</Badge>}
        </div>
      </CardContent>
    </Card>
  );
};

const PreviewDialog = ({ file, open, onOpenChange, onDownload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !file) { setPreviewUrl(null); setError(false); return; }
    let revoke = null;
    setLoading(true); setError(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/files/${file.id}`, { withCredentials: true, responseType: 'blob' });
        // Force correct blob type so iframe/img can render (some servers return octet-stream)
        const mime = file.content_type
          || (file.original_filename?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
        const typedBlob = new Blob([res.data], { type: mime });
        const url = URL.createObjectURL(typedBlob);
        revoke = url;
        setPreviewUrl(url);
      } catch { setError(true); }
      finally { setLoading(false); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, file]);

  const isImage = file?.content_type?.startsWith('image/');
  const isPdf = file?.content_type?.includes('pdf');
  const isText = file?.content_type?.startsWith('text/') || /\.(txt|csv|md|log|json)$/i.test(file?.original_filename || '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl w-[95vw] max-h-[95vh] p-0 flex flex-col overflow-y-auto">
        <DialogHeader className="px-5 py-3 border-b border-zinc-800 flex-row items-center justify-between">
          <DialogTitle className="text-white truncate pr-4">{file?.original_filename}</DialogTitle>
          <div className="flex items-center gap-2">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
                data-testid="preview-open-new-tab"
              >
                <ExternalLink className="w-3 h-3" /> Nouvel onglet
              </a>
            )}
            <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300"
              onClick={() => file && onDownload(file)} data-testid="preview-download">
              <Download className="w-3 h-3 mr-1" /> Télécharger
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto bg-zinc-950 p-4 min-h-[50vh]">
          {loading && (
            <div className="h-full flex items-center justify-center text-zinc-500">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="h-full flex items-center justify-center text-red-400">Erreur lors du chargement</div>
          )}
          {!loading && !error && previewUrl && isImage && (
            <img src={previewUrl} alt={file?.original_filename} className="max-w-full mx-auto rounded" />
          )}
          {!loading && !error && previewUrl && isPdf && (
            <iframe src={previewUrl} title="PDF preview" className="w-full h-[75vh] bg-white rounded" />
          )}
          {!loading && !error && previewUrl && isText && (
            <TextPreview url={previewUrl} />
          )}
          {!loading && !error && previewUrl && !isImage && !isPdf && !isText && (
            <div className="h-full flex flex-col items-center justify-center text-zinc-500 gap-3">
              <File className="w-12 h-12" />
              <p>Aperçu non disponible pour ce format</p>
              <p className="text-xs text-zinc-600">{file?.content_type || ''}</p>
              <Button size="sm" className="text-white"
                style={{ backgroundColor: 'var(--accent-color, #10B981)' }}
                onClick={() => file && onDownload(file)}>
                <Download className="w-3 h-3 mr-1" /> Télécharger
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TextPreview = ({ url }) => {
  const [text, setText] = useState('');
  useEffect(() => {
    fetch(url).then(r => r.text()).then(setText).catch(() => setText('Erreur'));
  }, [url]);
  return <pre className="text-xs text-zinc-300 whitespace-pre-wrap break-words font-mono">{text}</pre>;
};

const AssignDialog = ({ file, projects, open, onOpenChange, onSave }) => {
  const [projectId, setProjectId] = useState('');
  const [folder, setFolder] = useState('');
  const [subfolder, setSubfolder] = useState('');

  useEffect(() => {
    if (file) {
      setProjectId(file.project_id || '');
      setFolder(file.folder || '');
      setSubfolder(file.subfolder || '');
    }
  }, [file]);

  const subfolders = FOLDER_TREE.find(f => f.value === folder)?.subfolders || [];

  const handleSave = async () => {
    try {
      const params = new URLSearchParams();
      params.set('project_id', projectId || '');
      params.set('folder', folder || '');
      params.set('subfolder', subfolder || '');
      await axios.patch(`${API}/files/${file.id}/assign?${params.toString()}`, {}, { withCredentials: true });
      toast.success('Document assigné');
      onSave();
      onOpenChange(false);
    } catch { toast.error('Erreur'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto w-[95vw]">
        <DialogHeader><DialogTitle className="text-white">Assigner le document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">{file?.original_filename}</p>
          <div>
            <Label className="text-zinc-300">Projet</Label>
            <Select value={projectId || 'none'} onValueChange={(v) => setProjectId(v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun projet</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.property_title || 'Projet'} - {p.property_city || ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Dossier</Label>
              <Select value={folder || 'none'} onValueChange={(v) => { setFolder(v === 'none' ? '' : v); setSubfolder(''); }}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {FOLDER_TREE.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {subfolders.length > 0 && (
              <div>
                <Label className="text-zinc-300">Sous-dossier</Label>
                <Select value={subfolder || 'none'} onValueChange={(v) => setSubfolder(v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {subfolders.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Annuler</Button>
            <Button className="text-white" style={{ backgroundColor: 'var(--accent-color, #10B981)' }} onClick={handleSave}>Assigner</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UploadDialog = ({ open, onOpenChange, onUploadComplete, projects, defaultProject, defaultFolder, defaultSubfolder }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [folder, setFolder] = useState('');
  const [subfolder, setSubfolder] = useState('');

  useEffect(() => {
    if (open) {
      setProjectId(defaultProject || '');
      setFolder(defaultFolder || '');
      setSubfolder(defaultSubfolder || '');
    }
  }, [open, defaultProject, defaultFolder, defaultSubfolder]);

  const subfolders = FOLDER_TREE.find(f => f.value === folder)?.subfolders || [];

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.set('file', file);
        const params = [];
        if (projectId) params.push(`project_id=${projectId}`);
        if (folder) params.push(`folder=${folder}`);
        if (subfolder) params.push(`subfolder=${encodeURIComponent(subfolder)}`);
        const url = `${API}/upload${params.length ? '?' + params.join('&') : ''}`;
        await axios.post(url, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${fileList.length} fichier(s) téléversé(s)`);
      onUploadComplete();
      onOpenChange(false);
    } catch { toast.error('Erreur lors du téléversement'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto w-[95vw]">
        <DialogHeader><DialogTitle className="text-white">Téléverser des fichiers</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="text-zinc-400 text-xs">Projet</Label>
            <Select value={projectId || 'none'} onValueChange={(v) => setProjectId(v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.property_title || 'Projet'}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Dossier</Label>
            <Select value={folder || 'none'} onValueChange={(v) => { setFolder(v === 'none' ? '' : v); setSubfolder(''); }}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {FOLDER_TREE.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {subfolders.length > 0 && (
            <div>
              <Label className="text-zinc-400 text-xs">Sous-dossier</Label>
              <Select value={subfolder || 'none'} onValueChange={(v) => setSubfolder(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {subfolders.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? 'bg-zinc-800' : 'border-zinc-700 hover:border-zinc-600'}`}
          style={dragActive ? { borderColor: 'var(--accent-color, #10B981)' } : undefined}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); handleUpload(Array.from(e.dataTransfer.files)); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 animate-spin mb-3" style={{ color: 'var(--accent-color, #10B981)' }} />
              <p className="text-zinc-400">Téléversement en cours...</p>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
              <p className="text-zinc-400 mb-2">Glissez-déposez vos fichiers ici</p>
              <p className="text-zinc-600 text-sm mb-4">ou</p>
              <Button variant="outline" className="border-zinc-700" onClick={() => document.getElementById('file-input').click()}>Parcourir</Button>
              <input id="file-input" type="file" multiple className="hidden" onChange={(e) => handleUpload(Array.from(e.target.files))} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentsPage = () => {
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [activeFolder, setActiveFolder] = useState('all'); // 'all' | folder value
  const [activeSubfolder, setActiveSubfolder] = useState('');
  const [openFolders, setOpenFolders] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assignFile, setAssignFile] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const filesAreaRef = useRef(null);

  // Scroll to files area on mobile when a folder is selected (files are below the tree)
  const scrollToFiles = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && filesAreaRef.current) {
      setTimeout(() => {
        filesAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  };

  const selectFolder = (folder, subfolder = '') => {
    setActiveFolder(folder);
    setActiveSubfolder(subfolder);
    scrollToFiles();
  };

  const fetchData = async () => {
    try {
      const [fRes, pRes] = await Promise.all([
        axios.get(`${API}/files`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true })
      ]);
      setFiles(fRes.data);
      setProjects(pRes.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/files/${id}`, { withCredentials: true });
      toast.success('Fichier supprimé');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDownload = (file) => {
    try {
      // Direct-URL download with ?download=1 -> backend sends Content-Disposition: attachment.
      // Works reliably on desktop + Android + iOS Safari (no blob URL limitations).
      const url = `${API}/files/${file.id}?download=1`;
      downloadFromUrl(url, file.original_filename);
      toast.success('Téléchargement lancé');
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const handleAssign = (file) => {
    setAssignFile(file);
    setAssignOpen(true);
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  // Counts per folder/subfolder
  const counts = useMemo(() => {
    const c = { all: files.length, unfiled: 0 };
    files.forEach(f => {
      if (!f.folder) { c.unfiled += 1; return; }
      c[f.folder] = (c[f.folder] || 0) + 1;
      if (f.subfolder) {
        const k = `${f.folder}/${f.subfolder}`;
        c[k] = (c[k] || 0) + 1;
      }
    });
    return c;
  }, [files]);

  const filteredFiles = files.filter(f => {
    if (search && !f.original_filename?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterProject && f.project_id !== filterProject) return false;
    if (activeFolder === 'all') return true;
    if (activeFolder === 'unfiled') return !f.folder;
    if (f.folder !== activeFolder) return false;
    if (activeSubfolder && f.subfolder !== activeSubfolder) return false;
    return true;
  });

  const toggleFolder = (v) => setOpenFolders(prev => ({ ...prev, [v]: !prev[v] }));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Documents</h1>
          <p className="text-zinc-400 mt-1">Organisés par projet et dossier</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => setUploadOpen(true)} data-testid="upload-button">
            <Upload className="w-4 h-4 mr-2" /> Téléverser
          </Button>
        </div>
      </div>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploadComplete={fetchData}
        projects={projects}
        defaultProject={filterProject}
        defaultFolder={activeFolder !== 'all' && activeFolder !== 'unfiled' ? activeFolder : ''}
        defaultSubfolder={activeSubfolder}
      />
      <AssignDialog file={assignFile} projects={projects} open={assignOpen} onOpenChange={setAssignOpen} onSave={fetchData} />
      <PreviewDialog file={previewFile} open={previewOpen} onOpenChange={setPreviewOpen} onDownload={handleDownload} />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Folder tree sidebar */}
        <Card className="bg-zinc-900 border-zinc-800 self-start" data-testid="documents-folder-tree">
          <CardContent className="p-3">
            <div className="space-y-1">
              <button
                onClick={() => selectFolder('all')}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeFolder === 'all' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                }`}
                data-testid="folder-all"
              >
                <Home className="w-4 h-4" />
                <span className="flex-1 text-left">Tous</span>
                <span className="text-xs text-zinc-500">{counts.all || 0}</span>
              </button>
              {FOLDER_TREE.map(f => {
                const isOpen = !!openFolders[f.value];
                const isActive = activeFolder === f.value;
                const hasSubs = !!f.subfolders;
                return (
                  <div key={f.value}>
                    <div className={`flex items-stretch rounded-lg ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'}`}>
                      {hasSubs && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFolder(f.value); }}
                          className="flex-shrink-0 flex items-center justify-center px-3 py-3 sm:py-2 text-zinc-500 hover:text-white active:text-white"
                          aria-label={isOpen ? 'Réduire' : 'Développer'}
                          data-testid={`toggle-folder-${f.value}`}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          selectFolder(f.value);
                          // Auto-expand (never collapse) when activating a folder with subfolders
                          if (hasSubs && !isOpen) toggleFolder(f.value);
                        }}
                        className={`flex-1 flex items-center gap-2 ${hasSubs ? 'pl-1' : 'pl-3'} pr-3 py-3 sm:py-2 text-sm transition-colors min-w-0 ${
                          isActive ? 'text-white' : 'text-zinc-400 hover:text-white'
                        }`}
                        data-testid={`folder-${f.value}`}
                      >
                        {!hasSubs && <span className="w-4" />}
                        <Folder className="w-4 h-4 flex-shrink-0" style={isActive ? { color: 'var(--accent-color, #10B981)' } : undefined} />
                        <span className="flex-1 text-left truncate">{f.label}</span>
                        <span className="text-xs text-zinc-500">{counts[f.value] || 0}</span>
                      </button>
                    </div>
                    {hasSubs && isOpen && (
                      <div className="ml-6 mt-0.5 space-y-0.5">
                        {f.subfolders.map(sub => {
                          const isSubActive = isActive && activeSubfolder === sub;
                          return (
                            <button
                              key={sub}
                              type="button"
                              onClick={() => selectFolder(f.value, sub)}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 sm:py-1.5 rounded text-xs transition-colors ${
                                isSubActive ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'
                              }`}
                              data-testid={`subfolder-${f.value}-${sub}`}
                            >
                              <span className="flex-1 text-left truncate">{sub}</span>
                              <span className="text-[10px] text-zinc-600">{counts[`${f.value}/${sub}`] || 0}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                onClick={() => selectFolder('unfiled')}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mt-1 ${
                  activeFolder === 'unfiled' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:bg-zinc-800/50 hover:text-white'
                }`}
                data-testid="folder-unfiled"
              >
                <FolderOpen className="w-4 h-4" />
                <span className="flex-1 text-left">Non classés</span>
                <span className="text-xs text-zinc-500">{counts.unfiled || 0}</span>
              </button>

              {/* Par projet */}
              {projects.length > 0 && (
                <div className="mt-4 pt-3 border-t border-zinc-800">
                  <div className="px-3 mb-1 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Par projet</div>
                  <button
                    onClick={() => { setFilterProject(''); setActiveFolder('all'); setActiveSubfolder(''); scrollToFiles(); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      !filterProject ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                    }`}
                    data-testid="project-all"
                  >
                    <FolderIcon className="w-4 h-4" />
                    <span className="flex-1 text-left">Tous les projets</span>
                  </button>
                  {projects.map(p => {
                    const count = files.filter(f => f.project_id === p.id).length;
                    const isActive = filterProject === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => { setFilterProject(p.id); setActiveFolder('all'); setActiveSubfolder(''); scrollToFiles(); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive ? 'bg-emerald-500/10 text-emerald-400' : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                        }`}
                        data-testid={`project-folder-${p.id}`}
                        title={p.property_title || 'Projet'}
                      >
                        <FolderIcon className="w-4 h-4 flex-shrink-0" style={isActive ? { color: 'var(--accent-color, #10B981)' } : undefined} />
                        <span className="flex-1 text-left truncate text-xs">{p.property_title || p.id.slice(0, 8)}</span>
                        <span className="text-xs text-zinc-500">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Files grid */}
        <div ref={filesAreaRef} className="space-y-4 min-w-0 scroll-mt-20">
          {/* Mobile-only: current folder header with back button */}
          {(activeFolder !== 'all' || activeSubfolder || filterProject) && (
            <div className="lg:hidden flex items-center justify-between bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2" data-testid="mobile-folder-header">
              <div className="flex items-center gap-2 min-w-0">
                <FolderOpen className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-white truncate">
                  {filterProject && <span>📁 {projects.find(p => p.id === filterProject)?.property_title || 'Projet'} · </span>}
                  {activeFolder === 'unfiled' ? 'Non classés' : activeFolder !== 'all' ? (FOLDER_TREE.find(x => x.value === activeFolder)?.label || activeFolder) : 'Tous dossiers'}
                  {activeSubfolder && <span className="text-zinc-500"> / {activeSubfolder}</span>}
                  <span className="text-xs text-zinc-500 ml-2">({filteredFiles.length})</span>
                </span>
              </div>
              <button
                onClick={() => { setFilterProject(''); selectFolder('all'); }}
                className="text-xs text-zinc-400 hover:text-white underline-offset-2 hover:underline flex-shrink-0 ml-2"
                data-testid="back-to-all-folders"
              >
                ← Tout
              </button>
            </div>
          )}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input placeholder="Rechercher un fichier..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <Select value={filterProject || 'all'} onValueChange={(v) => setFilterProject(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full sm:w-56 bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Tous les projets" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les projets</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.property_title || 'Projet'}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-48 bg-zinc-800 rounded-lg animate-pulse" />)}
            </div>
          ) : filteredFiles.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredFiles.map(file => (
                <FileCard key={file.id} file={file} projects={projects} onDelete={handleDelete} onDownload={handleDownload} onAssign={handleAssign} onPreview={handlePreview} />
              ))}
            </div>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-16 text-center">
                <FolderOpen className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Aucun document</h3>
                <p className="text-zinc-500">Téléversez vos premiers fichiers dans ce dossier</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
